import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { ArrowRight, ArrowLeft, Loader2, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();

  // Step: "info" or "verify"
  const [step, setStep] = useState("info");

  // Form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");

  // Verification
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // UI state
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Format phone to E.164
  const formatPhone = (raw) => {
    const digits = raw.replace(/\D/g, "");
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    if (raw.startsWith("+")) return raw.replace(/[^\d+]/g, "");
    return `+${digits}`;
  };

  // Step 1: Submit info and send verification code
  const handleSendCode = async (e) => {
    if (e) e.preventDefault();
    setError("");

    if (!name.trim()) { setError("Full name is required"); return; }
    if (!email.trim()) { setError("Email is required"); return; }
    if (!phone.trim()) { setError("Phone number is required"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }

    const formatted = formatPhone(phone);
    if (formatted.length < 11) { setError("Please enter a valid phone number"); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/send-verification`, { phone: formatted });
      setCodeSent(true);
      setStep("verify");
      startCooldown();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to send verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Resend code
  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError("");
    const formatted = formatPhone(phone);
    try {
      await axios.post(`${API}/api/auth/send-verification`, { phone: formatted });
      startCooldown();
    } catch (err) {
      setError("Failed to resend code. Please try again.");
    }
  };

  const startCooldown = () => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  // Step 2: Verify code and register
  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    setError("");

    if (code.length !== 6) { setError("Please enter the 6-digit code"); return; }

    setLoading(true);
    try {
      // First verify the code
      const formatted = formatPhone(phone);
      await axios.post(`${API}/api/auth/verify-code`, { phone: formatted, code });

      // Then register
      await register(email, password, name, formatted, code);
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen">
      <div className="flex flex-col justify-center w-full max-w-md px-8 py-12 bg-white">
        <div className="mb-10">
          <img src="/logo.svg" alt="Relay" className="h-28 w-auto" />
        </div>

        {step === "info" && (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-semibold font-heading mb-1.5" style={{ color: "#0F1419" }}>Create account</h1>
              <p className="text-slate-500 text-sm">Get started with Relay today</p>
            </div>

            <form onSubmit={handleSendCode} className="space-y-4" data-testid="register-form">
              {error && (
                <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="register-error">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "#0F1419" }}>Full Name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} className="input" placeholder="John Smith" required data-testid="register-name-input" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "#0F1419" }}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="input" placeholder="you@example.com" required data-testid="register-email-input" />
                <p className="text-xs text-slate-500 mt-1.5 flex items-start gap-1.5">
                  <ShieldCheck size={12} className="shrink-0 mt-0.5" style={{ color: "#FF5C35" }} />
                  <span>Use the same email address you use with FollowUp Boss for seamless integration.</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "#0F1419" }}>Phone Number</label>
                <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="input" placeholder="(702) 555-0123" required data-testid="register-phone-input" />
                <p className="text-xs text-slate-400 mt-1">We'll send a verification code to this number.</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "#0F1419" }}>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="input" placeholder="Min. 6 characters" required data-testid="register-password-input" />
              </div>

              <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-2.5" data-testid="register-submit-button">
                {loading ? (
                  <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" />Sending code...</span>
                ) : (
                  <span className="flex items-center gap-2">Continue <ArrowRight size={16} /></span>
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-slate-500">
              Already have an account?{" "}
              <Link to="/login" className="font-medium hover:underline" style={{ color: "#FF5C35" }} data-testid="login-link">Sign in</Link>
            </p>
          </>
        )}

        {step === "verify" && (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-semibold font-heading mb-1.5" style={{ color: "#0F1419" }}>Verify your phone</h1>
              <p className="text-slate-500 text-sm">
                We sent a 6-digit code to <strong className="text-slate-700">{phone}</strong>
              </p>
            </div>

            <form onSubmit={handleVerifyAndRegister} className="space-y-4">
              {error && (
                <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "#0F1419" }}>Verification Code</label>
                <input
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="input text-center text-2xl font-mono tracking-[0.3em]"
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  data-testid="register-code-input"
                />
              </div>

              <button type="submit" disabled={loading || code.length !== 6} className="btn-primary w-full justify-center py-2.5">
                {loading ? (
                  <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" />Verifying...</span>
                ) : (
                  "Create account"
                )}
              </button>

              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={() => { setStep("info"); setError(""); setCode(""); }}
                  className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0}
                  className="text-sm font-medium disabled:text-slate-400"
                  style={resendCooldown > 0 ? {} : { color: "#FF5C35" }}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>

      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1756435292384-1bf32eff7baf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwaG91c2V8ZW58MHx8fHwxNzc1MzM5MTM0fDA&ixlib=rb-4.1.0&q=85" alt="Modern real estate" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom right, rgba(15,20,25,0.75), rgba(255,92,53,0.25))" }} />
      </div>
    </div>
  );
}
